// =============================================================================
// Error Codes — used as AppError codes and SAFE_ERROR_CODES
// =============================================================================

export const ERROR_CODES = {
  // Game engine
  MATCH_NOT_FOUND: 'match_not_found',
  // A match that exists but has already settled (backgammon board-sync /
  // resign past the replay window). The client treats it, like
  // MATCH_NOT_FOUND, as a terminal refusal of the current match.
  MATCH_OVER: 'match_over',
  MATCH_NOT_IN_PROGRESS: 'match_not_in_progress',
  NOT_YOUR_TURN: 'not_your_turn',

  // Economy
  USER_NOT_FOUND: 'user_not_found',
  INSUFFICIENT_ENERGY: 'insufficient_energy',
  INSUFFICIENT_COINS: 'insufficient_coins',
  INSUFFICIENT_GEMS: 'insufficient_gems',
  /**
   * Daily-login bonus claim attempted before the eligibility window
   * (`DAILY_LOGIN_BONUS_ELIGIBILITY_HOURS`) has elapsed since the last
   * claim. The controller surfaces `retryAfterSeconds` so the client can
   * render a countdown.
   */
  DAILY_BONUS_NOT_YET_AVAILABLE: 'daily_bonus_not_yet_available',
  /**
   * Match-end "2× coins" doubler (#416, `docs/ECONOMY.md` §4.4) —
   * the underlying match-end Transaction for this `(userId, matchId)` pair
   * does not exist. Either the matchId is wrong, the user wasn't a
   * participant, or the match never reached the rewarded `endMatch` path
   * (e.g. an abandoned/orphaned match where bots get the fallback payload).
   */
  MATCH_DOUBLER_MATCH_NOT_FOUND: 'match_doubler_match_not_found',
  /**
   * Match-end doubler — the Redis SSV reward token was missing or invalid
   * when the controller tried to consume it. Distinct from the generic
   * `AD_NOT_VERIFIED` so the mobile UI can render a doubler-specific
   * "couldn't verify ad" message without conflating it with the shop
   * energy/coins ad path.
   */
  MATCH_DOUBLER_AD_VERIFICATION_FAILED: 'match_doubler_ad_verification_failed',
  /**
   * Match-end doubler — bot user attempted to claim. Bots have no client
   * and can't legitimately reach this endpoint; returned as a 403
   * defence-in-depth in case a bot's JWT is somehow replayed.
   */
  MATCH_DOUBLER_BOT_FORBIDDEN: 'match_doubler_bot_forbidden',

  // Shop
  INVALID_PRODUCT: 'invalid_product',
  IAP_REQUIRED: 'iap_required',
  AD_REQUIRED: 'ad_required',
  AD_LIMIT_REACHED: 'ad_limit_reached',
  PURCHASE_FAILED: 'purchase_failed',
  MISSING_PARAMS: 'missing_params',
  INVALID_SIGNATURE: 'invalid_signature',
  INVALID_USER_ID: 'invalid_user_id',
  AD_NOT_VERIFIED: 'ad_not_verified',
  INVALID_REWARD: 'invalid_reward',
  ALREADY_CLAIMED: 'already_claimed',

  // IAP receipt verification (#9). Distinct codes so the mobile UI can map
  // each one to a tailored prompt (retry vs contact support vs check store).
  IAP_PLATFORM_REQUIRED: 'iap_platform_required',
  IAP_PLATFORM_INVALID: 'iap_platform_invalid',
  IAP_RECEIPT_REQUIRED: 'iap_receipt_required',
  IAP_RECEIPT_INVALID: 'iap_receipt_invalid',
  IAP_PRODUCT_NOT_FOUND: 'iap_product_not_found',
  IAP_PRODUCT_MISMATCH: 'iap_product_mismatch',
  IAP_PURCHASE_NOT_COMPLETED: 'iap_purchase_not_completed',
  IAP_VERIFIER_NOT_CONFIGURED: 'iap_verifier_not_configured',
  IAP_CLIENT_BALANCE_FORBIDDEN: 'iap_client_balance_forbidden',
  // Hard guard on the first-purchase-only `starter_pack` SKU. Server checks
  // `User.totalIapPurchases === 0` BEFORE the IAP grant runs — replays of a
  // legitimate first-time receipt are caught earlier by the
  // (platform, transactionId) idempotency upsert and surfaced as
  // `alreadyGranted: true`. Reaching this code means the user already has at
  // least one prior IAP grant on record (any SKU), which makes the bundle
  // ineligible.
  IAP_STARTER_PACK_ALREADY_PURCHASED: 'iap_starter_pack_already_purchased',
  // Restore purchases (#409). Caps the receipt batch size on POST
  // /shop/restore so a single request cannot fan out into hundreds of
  // verifier round-trips.
  IAP_RESTORE_BATCH_TOO_LARGE: 'iap_restore_batch_too_large',
  IAP_RESTORE_RECEIPTS_REQUIRED: 'iap_restore_receipts_required',
  // IAP refund webhooks (#405). Surface only on inbound webhook routes
  // (`/api/webhooks/apple/iap`, `/api/webhooks/google/iap`); never reaches
  // the mobile client, which has no UI surface for refund failures.
  IAP_WEBHOOK_PAYLOAD_REQUIRED: 'iap_webhook_payload_required',
  IAP_WEBHOOK_VERIFICATION_FAILED: 'iap_webhook_verification_failed',
  IAP_WEBHOOK_AUTH_REQUIRED: 'iap_webhook_auth_required',
  IAP_WEBHOOK_AUTH_INVALID: 'iap_webhook_auth_invalid',

  // Auth
  UNAUTHORIZED: 'unauthorized',
  NOT_AUTHENTICATED: 'not_authenticated',
  DEVICE_ID_REQUIRED: 'device_id_required',
  USERNAME_GENERATION_FAILED: 'username_generation_failed',
  PROVIDER_TOKEN_REQUIRED: 'provider_token_required',
  INVALID_PROVIDER: 'invalid_provider',
  FIREBASE_NOT_CONFIGURED: 'firebase_not_configured',
  TOKEN_EXPIRED: 'token_expired',
  TOKEN_REVOKED: 'token_revoked',
  INVALID_TOKEN: 'invalid_token',
  PROVIDER_MISMATCH: 'provider_mismatch',
  SOCIAL_ALREADY_LINKED: 'social_already_linked',
  // Guest -> social merge (POST /auth/merge). Distinct codes so the mobile UI
  // can differentiate "already linked, want to merge?" (handled on /auth/link
  // 409) from "merge preconditions failed" surfaced here.
  MERGE_NOT_GUEST: 'merge_not_guest',
  MERGE_TARGET_NOT_FOUND: 'merge_target_not_found',
  MERGE_TARGET_IS_GUEST: 'merge_target_is_guest',
  MERGE_USER_IN_MATCH: 'merge_user_in_match',
  MERGE_SELF: 'merge_self',
  MERGE_FORBIDDEN: 'merge_forbidden',
  REFRESH_TOKEN_REQUIRED: 'refresh_token_required',
  INVALID_REFRESH_TOKEN: 'invalid_refresh_token',
  ACCOUNT_BANNED: 'account_banned',
  AUTH_TOKEN_REVOKED: 'auth_token_revoked',
  MISSING_AUTH_HEADER: 'missing_auth_header',
  INVALID_API_TOKEN: 'invalid_api_token',
  REFRESH_NOT_FOR_API: 'refresh_not_for_api',
  // Multi-game (#610, epic #600)
  GAME_MISMATCH: 'game_mismatch',
  GAME_NOT_FOUND: 'game_not_found',
  // Distinct from GAME_NOT_FOUND on purpose: the game IS admissible, it just
  // does not declare this admin surface. The admin SPA self-heals a stranded
  // `X-Game-Id` off GAME_NOT_FOUND, so collapsing the two would reset the
  // operator's active game every time they deep-linked a surface that game
  // legitimately lacks.
  GAME_SURFACE_NOT_FOUND: 'game_surface_not_found',
  ADMIN_REQUIRED: 'admin_required',
  ADMIN_CREDENTIALS_REQUIRED: 'admin_credentials_required',
  INVALID_ADMIN_CREDENTIALS: 'invalid_admin_credentials',
  ADMIN_ACCESS_DENIED: 'admin_access_denied',
  ACCOUNT_DISABLED: 'account_disabled',
  INVALID_MFA_CODE: 'invalid_mfa_code',
  MFA_CHALLENGE_EXPIRED: 'mfa_challenge_expired',
  MFA_NOT_ENROLLED: 'mfa_not_enrolled',
  MFA_ALREADY_ENROLLED: 'mfa_already_enrolled',
  MFA_CODE_REQUIRED: 'mfa_code_required',
  MFA_ENROLLMENT_PENDING_NOT_FOUND: 'mfa_enrollment_pending_not_found',
  // Admin refresh-token sessions (Remember Me / extended sessions)
  ADMIN_REFRESH_REQUIRED: 'admin_refresh_required',
  ADMIN_REFRESH_INVALID: 'admin_refresh_invalid',
  ADMIN_REFRESH_REUSE_DETECTED: 'admin_refresh_reuse_detected',
  ADMIN_REFRESH_CSRF_REJECTED: 'admin_refresh_csrf_rejected',

  // User
  INVALID_USERNAME: 'invalid_username',
  INVALID_DISPLAY_NAME: 'invalid_display_name',
  DISPLAY_NAME_BLOCKED: 'display_name_blocked',
  USERNAME_TAKEN: 'username_taken',
  INVALID_AVATAR: 'invalid_avatar',
  INVALID_LANGUAGE: 'invalid_language',
  INVALID_PUSH_TOKEN: 'invalid_push_token',
  INVALID_PLATFORM: 'invalid_platform',

  // Username rename (#367) — split codes so the mobile UI can map each one
  // to a distinct localized message + retry hint.
  USERNAME_RESERVED: 'username_reserved',
  USERNAME_RENAME_COOLDOWN: 'username_rename_cooldown',
  USERNAME_UNAVAILABLE: 'username_unavailable',

  // Friends
  VALID_USER_ID_REQUIRED: 'valid_user_id_required',
  CANNOT_FRIEND_SELF: 'cannot_friend_self',
  ALREADY_FRIENDS: 'already_friends',
  REQUEST_PENDING: 'request_pending',
  CANNOT_SEND_REQUEST: 'cannot_send_request',
  NO_PENDING_REQUEST: 'no_pending_request',
  INVALID_FRIEND_ID: 'invalid_friend_id',
  FRIENDSHIP_NOT_FOUND: 'friendship_not_found',
  INVITE_REQUIRED: 'invite_required',
  INVITE_EXPIRED: 'invite_expired',
  CANNOT_ACCEPT_OWN_INVITE: 'cannot_accept_own_invite',

  // Matchmaking state guards
  ALREADY_IN_QUEUE: 'already_in_queue',
  ALREADY_IN_MATCH: 'already_in_match',
  RECONNECT_FAILED: 'reconnect_failed',
  /**
   * The client asked to queue with a match option the ACTIVE game does not
   * declare (`GameModule.matchOptions.values`). Never trust the client with
   * a value that shapes the match — an undeclared one is refused, not
   * silently defaulted, so a tampered payload is visible instead of quietly
   * becoming somebody else's match length.
   */
  INVALID_MATCH_OPTION: 'invalid_match_option',

  // Match
  NOT_PARTICIPANT: 'not_participant',
  NOT_FOUND: 'not_found',
  FORBIDDEN: 'forbidden',
  CONFLICT: 'conflict',
  SERVICE_UNAVAILABLE: 'service_unavailable',

  // WebSocket
  RATE_LIMIT: 'rate_limit',
  FRIEND_ID_REQUIRED: 'friend_id_required',
  NO_COMPLETED_MATCH: 'no_completed_match',
  QUICK_CHAT_NOT_AVAILABLE: 'quick_chat_not_available',
  /** In-match chat / gifts (#792, #793): the caller is not seated at that live match. */
  MATCH_SOCIAL_NOT_AVAILABLE: 'match_social_not_available',
  /** A gift addressed to someone who is not seated at the caller's table. */
  GIFT_TARGET_INVALID: 'gift_target_invalid',
  NOT_FRIENDS: 'not_friends',
  FRIEND_NOT_ONLINE: 'friend_not_online',
  CHALLENGE_ID_REQUIRED: 'challenge_id_required',
  CHALLENGE_NOT_FOUND: 'challenge_not_found',
  CHALLENGE_ALREADY_PENDING: 'challenge_already_pending',
  NOT_AUTHORIZED_CHALLENGE: 'not_authorized_challenge',
  CHALLENGER_NO_ENERGY: 'challenger_no_energy',
  NOT_ENOUGH_ENERGY_ACCEPT: 'not_enough_energy_accept',
  OPPONENT_NO_ENERGY: 'opponent_no_energy',
  CHALLENGER_DISCONNECTED: 'challenger_disconnected',
  /** A game that seats more than two players has no 1v1 challenge/rematch (#679). */
  CHALLENGE_NOT_SUPPORTED: 'challenge_not_supported',
  /** Friend tables exist only for games that seat more than two (#772). */
  FRIEND_TABLE_NOT_SUPPORTED: 'friend_table_not_supported',
  /** The friend-table invite is gone, already resolved, or not yours to answer. */
  TABLE_INVITE_NOT_FOUND: 'table_invite_not_found',
  /** Friends / partner do not fit the table (too many, duplicates, a partner who is not invited, a partner in a solo option). */
  TABLE_INVITE_INVALID: 'table_invite_invalid',
  /** You are already hosting or sitting in another friend-table lobby. */
  ALREADY_IN_TABLE_INVITE: 'already_in_table_invite',

  // Validation / generic
  VALIDATION_ERROR: 'validation_error',
  VALIDATION_FAILED: 'validation_failed',
  DUPLICATE_RESOURCE: 'duplicate_resource',
  UNEXPECTED_ERROR: 'unexpected_error',
  INTERNAL_SERVER_ERROR: 'internal_server_error',
  NO_AD_COMPLETION: 'no_ad_completion',
  INVALID_REWARD_TYPE: 'invalid_reward_type',
  REWARD_ALREADY_CLAIMED: 'reward_already_claimed',
  PRODUCT_ID_REQUIRED: 'product_id_required',
  REWARD_TYPE_INVALID: 'reward_type_invalid',
  INVALID_STATE: 'invalid_state',
  ALREADY_USED: 'already_used',

  // Admin
  ADMIN_FIELDS_REQUIRED: 'admin_fields_required',
  INVALID_ADMIN_ROLE: 'invalid_admin_role',
  AUDIT_INVALID_INPUT: 'audit_invalid_input',
  AUDIT_INVALID_RANGE: 'audit_invalid_range',
  AUDIT_INVALID_ACTOR_ID: 'audit_invalid_actor_id',
  AUDIT_WRITE_FAILED: 'audit_write_failed',
  INVALID_CURRENCY: 'invalid_currency',
  INVALID_DATE_RANGE: 'invalid_date_range',
  INVALID_CURRENCY_DELTA: 'invalid_currency_delta',
  NO_CURRENCY_CHANGE: 'no_currency_change',

  // Admin account management (superadmin-only CRUD on AdminUser collection)
  ADMIN_USER_NOT_FOUND: 'admin_user_not_found',
  ADMIN_USERNAME_TAKEN: 'admin_username_taken',
  ADMIN_SELF_EDIT_FORBIDDEN: 'admin_self_edit_forbidden',
  ADMIN_PASSWORD_TOO_SHORT: 'admin_password_too_short',
  ADMIN_INVALID_ROLE: 'admin_invalid_role',
  ADMIN_NO_CHANGES: 'admin_no_changes',

  // Remote App Configuration (#336 epic)
  CONFIG_VALIDATION_FAILED: 'config_validation_failed',

  // Admin Broadcast Notifications
  BROADCAST_VALIDATION_FAILED: 'broadcast_validation_failed',
  BROADCAST_NO_RECIPIENTS: 'broadcast_no_recipients',
  BROADCAST_FIREBASE_DISABLED: 'broadcast_firebase_disabled',
  BROADCAST_NOT_FOUND: 'broadcast_not_found',

  // Support messages (Settings → Contact Us)
  SUPPORT_VALIDATION_FAILED: 'support_validation_failed',
  SUPPORT_RATE_LIMITED: 'support_rate_limited',
  SUPPORT_MESSAGE_NOT_FOUND: 'support_message_not_found',
  SUPPORT_INVALID_STATUS: 'support_invalid_status',

  // Direct Messages (#308 epic)
  DM_NOT_FRIENDS: 'dm_not_friends',
  DM_BLOCKED: 'dm_blocked',
  DM_RATE_LIMITED: 'dm_rate_limited',
  DM_MESSAGE_TOO_LONG: 'dm_message_too_long',
  DM_CONVERSATION_NOT_FOUND: 'dm_conversation_not_found',
  DM_MESSAGE_NOT_FOUND: 'dm_message_not_found',
  DM_REPORT_REASON_INVALID: 'dm_report_reason_invalid',
  DM_SELF_TARGET_FORBIDDEN: 'dm_self_target_forbidden',
  DM_CANNOT_LIKE_OWN_MESSAGE: 'dm_cannot_like_own_message',

  // Admin moderation queue (#317 — report triage + auto-filter flags)
  MODERATION_REPORT_NOT_FOUND: 'moderation_report_not_found',
} as const;

// =============================================================================
// Message Keys — used as i18n keys for user-facing text (not error codes)
// =============================================================================

export const MESSAGE_KEYS = {
  NOTIF_REMATCH_TITLE: 'notif_rematch_title',
  NOTIF_CHALLENGE_TITLE: 'notif_challenge_title',
  NOTIF_CHALLENGE_BODY: 'notif_challenge_body',
  NOTIF_TABLE_INVITE_TITLE: 'notif_table_invite_title',
  NOTIF_TABLE_INVITE_BODY: 'notif_table_invite_body',
} as const;
